import {
  closeModal,
  Modal,
  openModal,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useState } from "react";
import { QueryBuilderConfig, QueryGroup } from "../../data-model/queryBuilder";
import { CrossIcon } from "../../svg/cross-icon";
import BooleanSwitch from "../booleanSwitch/booleanSwitch";
import QueryBuilderConditionRow from "./QueryBuilderConditionRow";

const DELETE_GROUP_MODAL_ID = "delete-group-confirmation-modal";

interface QueryBuilderGroupProps {
  group: QueryGroup;
  config: QueryBuilderConfig;
  isRoot?: boolean;
  onFieldChange: (conditionId: string, fieldValue: string) => void;
  onOperatorChange: (
    conditionId: string,
    operatorValue: string,
    fieldValue: string,
  ) => void;
  onValueChange: (conditionId: string, newValue: string | string[]) => void;
  onToggleConjunction: (groupId: string, conjunction: "AND" | "OR") => void;
  onAddCondition: (groupId: string) => void;
  onAddNestedGroup: (groupId: string) => void;
  onRemove: (nodeId: string) => void;
}

const QueryBuilderGroup = ({
  group,
  config,
  isRoot = false,
  onFieldChange,
  onOperatorChange,
  onValueChange,
  onToggleConjunction,
  onAddCondition,
  onAddNestedGroup,
  onRemove,
}: QueryBuilderGroupProps) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (showDeleteConfirm) {
      openModal(DELETE_GROUP_MODAL_ID);
    }
  }, [showDeleteConfirm]);

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    onRemove(group.id);
    closeModal(DELETE_GROUP_MODAL_ID);
    setShowDeleteConfirm(false);
  };

  const handleDeleteCancel = () => {
    closeModal(DELETE_GROUP_MODAL_ID);
    setShowDeleteConfirm(false);
  };

  const hasChildren = group.children.length > 0;

  return (
    <div
      className={`relative flex flex-col gap-3 rounded-lg border border-gray-200 p-4 ${
        !isRoot ? "ml-8" : ""
      }`}
    >
      <div className="flex items-center justify-between">
        <BooleanSwitch
          value={group.conjunction === "AND"}
          onChange={(val) => onToggleConjunction(group.id, val ? "AND" : "OR")}
          onLabel="AND"
          offLabel="OR"
        />
        {!isRoot && (
          <button
            type="button"
            onClick={handleDeleteClick}
            className="cursor-pointer rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
            aria-label="Remove group"
          >
            <CrossIcon width="18" height="18" fillColor="#9ca3af" />
          </button>
        )}
      </div>

      {hasChildren && (
        <div className="relative ml-5">
          {/* Vertical connector line - from switch down to top of Add Condition row (gap-3 below container) */}
          <div
            className="absolute -top-3 left-0 w-px bg-gray-300"
            style={{ height: "calc(100% + 1.5rem)" }}
          />

          <div className="flex flex-col gap-2">
            {group.children.map((child, index) => (
              <div key={child.id} className="relative flex items-stretch">
                {/* Horizontal branch line - w-6 meets condition card; w-14 reaches nested group card (ml-6 + ml-8) */}
                <div
                  className={`absolute left-0 top-1/2 h-px -translate-y-1/2 bg-gray-300 ${
                    child.type === "condition" ? "w-6" : "w-14"
                  }`}
                />

                <div className="ml-6 flex-1">
                  {child.type === "condition" ? (
                    <QueryBuilderConditionRow
                      condition={child}
                      config={config}
                      onFieldChange={onFieldChange}
                      onOperatorChange={onOperatorChange}
                      onValueChange={onValueChange}
                      onRemove={onRemove}
                    />
                  ) : (
                    <QueryBuilderGroup
                      group={child}
                      config={config}
                      onFieldChange={onFieldChange}
                      onOperatorChange={onOperatorChange}
                      onValueChange={onValueChange}
                      onToggleConjunction={onToggleConjunction}
                      onAddCondition={onAddCondition}
                      onAddNestedGroup={onAddNestedGroup}
                      onRemove={onRemove}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="ml-5 flex items-center gap-4">
        <div
          role="button"
          tabIndex={0}
          onClick={() => onAddCondition(group.id)}
          className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-800"
        >
          + Add Condition
        </div>
        <div
          role="button"
          tabIndex={0}
          onClick={() => onAddNestedGroup(group.id)}
          className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-800"
        >
          + Add Nested Group
        </div>
      </div>
      {showDeleteConfirm && (
        <Modal
          dialogId={DELETE_GROUP_MODAL_ID}
          title="Delete Group"
          saveButtonText="Yes, Delete"
          cancelText="Cancel"
          onSave={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          onClose={handleDeleteCancel}
        >
          <div className="flex flex-col items-center justify-center gap-3 py-2">
            <div className="text-center text-sm text-primaryGray-3">
              Are you sure you want to delete this group? All conditions within
              it will also be removed.
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default QueryBuilderGroup;
